import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import Button from "../components/ui/button/Button";
import { 
  Plus, Building2, CircleOff, CheckCircle2, 
  IndianRupee, ChevronRight, UserCircle, Activity, GraduationCap, ArrowUp, User, Layers
} from "lucide-react";
import enquiryService from "../api/enquiryService";

export default function ClientList({ isDark, activeFilters, onDataLoaded }) {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showScrollTop, setShowScrollTop] = useState(false);
  
  const hasFetched = useRef(false);

  const secondaryTextColor = isDark ? 'text-slate-300' : 'text-slate-600';
  // Brand Blue Colors
  const brandBlue = isDark ? 'text-[#5A9AFF]' : 'text-[#2D4D94]';
  const brandBlueIcon = isDark ? '#5A9AFF' : '#2D4D94';

  useEffect(() => {
    const fetchEnquiries = async () => {
      try {
        setLoading(true);
        const data = await enquiryService.getAllEnquiries();
        setClients(data);
        if (onDataLoaded) onDataLoaded(data);
      } catch (error) {
        console.error("Failed to fetch enquiries:", error);
      } finally {
        setLoading(false);
      }
    };

    if (!hasFetched.current) {
      fetchEnquiries();
      hasFetched.current = true;
    }

    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [onDataLoaded]); 

  const filteredData = useMemo(() => {
    // 1. Filter the data based on active filters
    const filtered = clients.filter(item => {
      const globalSearchValue = activeFilters?.global_search?.[0]?.toLowerCase() || "";
      
      const matchesGlobal = !globalSearchValue || [
        item.id,
        item.enquirer_name,
        item.enq_email,
        item.enq_number,
        item.enquirer_company,
        item.college_name,
        item.course_taken,
        item.passing_grade,
        item.job_title,
        item.job_company_location,
        item.job_field,
        item.enquiry_status,
        item.budget_range,
        item.brand_name,
        item.service_name
      ].some(val => 
        String(val || "").toLowerCase().includes(globalSearchValue)
      );

      const matchesBrand = !activeFilters?.brand_name?.length || activeFilters.brand_name.includes(item.brand_name);
      const matchesService = !activeFilters?.service_name?.length || activeFilters.service_name.includes(item.service_name);
      const matchesStatus = !activeFilters?.enquiry_status?.length || activeFilters.enquiry_status.includes(item.enquiry_status);
      const matchesBudget = !activeFilters?.budget_range?.length || activeFilters.budget_range.includes(item.range_label?.trim());

      return matchesGlobal && matchesBrand && matchesService && matchesStatus && matchesBudget;
    });

    // 2. Sort the data: Ongoing at top, Closed/Completed at bottom, Alphabetical by name within groups
    return filtered.sort((a, b) => {
      const statusA = a.enquiry_status?.toLowerCase().trim();
      const statusB = b.enquiry_status?.toLowerCase().trim();
      
      const isFinishedA = statusA === 'closed' || statusA === 'completed';
      const isFinishedB = statusB === 'closed' || statusB === 'completed';

      if (isFinishedA && !isFinishedB) return 1;
      if (!isFinishedA && isFinishedB) return -1;
      
      // Secondary sort: Alphabetical by Name
      return (a.enquirer_name || "").localeCompare(b.enquirer_name || "");
    });
  }, [clients, activeFilters]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getStatusConfig = (status) => {
    const tagBase = "px-3 py-1.5 text-[11px] font-black border rounded-lg inline-flex items-center gap-1.5 transition-colors w-fit whitespace-nowrap";
    const normalizedStatus = status?.toLowerCase().trim();

    switch (normalizedStatus) {
      case 'closed':
        return { 
          label: 'Closed', 
          icon: <CircleOff size={11} />, 
          classes: `${tagBase} text-orange-500 border-orange-500/20 bg-orange-500/5` 
        };
      case 'completed':
        return { 
          label: 'Completed', 
          icon: <CheckCircle2 size={11} />, 
          classes: `${tagBase} text-emerald-500 border-emerald-500/20 bg-emerald-500/5` 
        };
      case 'in progress':
      case 'inprogress':
        return { 
          label: 'In Progress', 
          icon: <Activity size={11} />, 
          classes: `${tagBase} text-blue-400 border-blue-500/20 bg-blue-500/10` 
        };
      case 'started with advance payment':
      case 'started with adv. payment':
      case 'advanced payment':
        return { 
          label: 'Adv. Payment', 
          icon: <IndianRupee size={11} />, 
          classes: `${tagBase} text-emerald-400 border-emerald-500/20 bg-emerald-500/10` 
        };
      case 'just connected':
      case 'justconnected':
        return { 
          label: 'Just Connected', 
          icon: <User size={11} />, 
          classes: `${tagBase} ${isDark ? 'text-[#5A9AFF] border-[#5A9AFF]/20 bg-[#5A9AFF]/10' : 'text-[#2D4D94] border-[#2D4D94]/20 bg-[#2D4D94]/10'}` 
        };
      default:
        return { label: status, icon: null, classes: tagBase };
    }
  };

  return (
    <div className={`p-1 sm:p-6 space-y-6 min-h-screen transition-colors duration-300 ${isDark ? 'bg-slate-800/0' : 'bg-slate-100/0'}`}>
      
      <div className="relative flex flex-col items-center justify-center min-h-[120px] sm:min-h-[100px] sm:-mt-8 max-w-7xl mx-auto w-full px-4">
        <div className="text-center">
          <h2 className={`text-2xl font-brand-heading font-bold uppercase tracking-tighter leading-tight ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
            <span className="text-orange-600">Client</span> <span className={brandBlue}> Enquiry</span>
          </h2>
          
          <div className="flex items-center justify-center gap-3 mt-0">
            <div className={`h-px w-4 ${isDark ? 'bg-slate-400' : 'bg-slate-400'}`} />
            <p className={`text-[13px] uppercase tracking-[0.1em] ${secondaryTextColor}`}>
              Enquiries & Conversations
            </p>
            <div className={`h-px w-4 ${isDark ? 'bg-slate-400' : 'bg-slate-400'}`} />
          </div>
        </div>
        
        <div className="mt-4 sm:mt-0 sm:absolute sm:right-4 sm:top-1/2 sm:-translate-y-1/2 flex items-center">
          <Button 
            isDark={isDark} 
            onClick={() => navigate('/enquiry/new')} 
            className={`
              group flex items-center px-5 py-2.5 rounded-xl font-bold uppercase tracking-wider text-xs transition-all border shadow-sm hover:shadow-md active:scale-95 whitespace-nowrap
              ${isDark ? 'bg-slate-900/50 border-slate-700 hover:bg-slate-800' : 'bg-white border-slate-200 hover:bg-slate-50'}
            `}
          >
            <Plus size={18} strokeWidth={3} className="text-orange-600 transition-transform duration-500 ease-out group-hover:rotate-90" /> 
            <span className={`ml-2 ${brandBlue}`}>New Enquiry</span>
          </Button>
        </div>
      </div>

      <div className={`rounded-2xl border shadow-sm overflow-hidden max-w-7xl mx-auto w-full transition-all ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}`}>
        <div className="overflow-x-auto w-full">
          <Table className="min-w-[1000px] w-full text-left">
            <TableHeader className={isDark ? 'bg-slate-950/50' : 'bg-slate-50/50'}>
              <TableRow className={isDark ? 'border-slate-800' : 'border-slate-100'}>
                <TableCell isHeader className={`px-6 py-4 text-[11px] font-black font-brand-heading uppercase tracking-widest ${secondaryTextColor}`}>Client Identity</TableCell>
                <TableCell isHeader className={`px-6 py-4 text-[11px] font-black font-brand-heading uppercase tracking-widest ${secondaryTextColor}`}>Brand & Service</TableCell>
                <TableCell isHeader className={`px-6 py-4 text-[11px] font-black font-brand-heading uppercase tracking-widest ${secondaryTextColor}`}>Enquiry Status</TableCell>
                <TableCell isHeader className="px-6 py-4 text-right"></TableCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} className="py-24 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <Activity className={`animate-pulse ${brandBlue}`} size={30} />
                        <span className={`uppercase text-[10px] font-bold tracking-[0.2em] ${secondaryTextColor}`}>Syncing Records...</span>
                      </div>
                    </TableCell>
                  </TableRow>
              ) : filteredData.length > 0 ? filteredData.map((client) => {
                const statusConfig = getStatusConfig(client.enquiry_status);
                return (
                  <TableRow 
                    key={client.id} 
                    className={`cursor-pointer transition-all group border-b last:border-none ${isDark ? 'hover:bg-slate-800 border-slate-800' : 'hover:bg-blue-50/30 border-slate-50'}`}
                    onClick={() => navigate(`/enquiry/${client.id}`)}
                  >
                    <TableCell className="px-6 py-5">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center border transition-colors ${isDark ? 'bg-slate-800 border-slate-700 text-slate-400 group-hover:border-blue-500/50' : 'bg-slate-50 border-slate-200 text-slate-400 group-hover:border-blue-200'}`}>
                          <UserCircle size={24} className={`transition-colors group-hover:${brandBlue}`} />
                        </div>
                        <div className="space-y-1">
                          <div className={`font-brand-body-bold tracking-tight text-sm ${isDark ? 'text-slate-100 group-hover:text-[#5A9AFF]' : 'text-slate-900 group-hover:text-[#2D4D94]'}`}>
                            {client.enquirer_name}
                          </div>
                          <div className={`flex items-center gap-1.5 text-[12px] tracking-widest ${secondaryTextColor}`}>
                            {client.enquirer_company ? (
                              <><Building2 size={14} className={brandBlue}/> {client.enquirer_company}</>
                            ) : client.college_name ? (
                              <><GraduationCap size={14} className={brandBlue}/> {client.college_name}</>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="px-6 py-5">
                        <div className={`text-[12px] flex gap-1.5 font-black tracking-widest ${secondaryTextColor} mb-0.5`}>
                           <Building2 size={14} className={brandBlue}/>{client.brand_name}
                        </div>
                        <div className={`text-[12px] flex items-center gap-1.5 font-brand-body-bold tracking-widest ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                           <Layers size={14} className={brandBlue}/> {client.service_name}
                        </div>
                    </TableCell>
                    <TableCell className="px-6 py-5">
                        <div className={statusConfig.classes}>
                          <span className={`w-1.5 h-1.5 rounded-full ${client.enquiry_status?.toLowerCase().includes('completed') || client.enquiry_status?.toLowerCase().includes('payment') ? 'bg-emerald-500 animate-pulse' : 'bg-current'}`} />
                          {statusConfig.icon} {statusConfig.label}
                        </div>
                    </TableCell>
                    <TableCell className="px-6 py-5 text-right">
                      <div className={`flex justify-end items-center group-hover:translate-x-1 transition-all ${isDark ? 'text-slate-700 group-hover:text-[#5A9AFF]' : 'text-slate-300 group-hover:text-[#2D4D94]'}`}>
                        <ChevronRight size={20} />
                      </div>
                    </TableCell>
                  </TableRow>
                );
              }) : (
                <TableRow>
                    <TableCell colSpan={4} className="py-24 text-center">
                        <div className="flex flex-col items-center gap-2 opacity-30">
                          <UserCircle size={40} className={isDark ? 'text-slate-500' : 'text-slate-400'} />
                          <p className={`text-[10px] font-black uppercase tracking-[0.3em] ${secondaryTextColor}`}>No Matching Records</p>
                        </div>
                    </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className={`fixed bottom-8 right-8 p-3 rounded-full shadow-2xl z-50 transition-all hover:scale-110 active:scale-95 ${
            isDark ? 'bg-[#5A9AFF] text-white' : 'bg-[#2D4D94] text-white'
          }`}
        >
          <ArrowUp size={20} />
        </button>
      )}
    </div>
  );
}